/**
 * Landing v3 — the leaderboard in the "Compare your stats with friends" tile (Figma
 * 15:1412, node 39:5632): "Leaderboard" header, a University / Global switch,
 * ranked rows with a mark, a name and mastery points. The switch is the one
 * interactive thing — it flips the list; nothing routes anywhere.
 *
 * LIVE (user 2026-09-28, reshaped 2026-09-30): the University tab reads
 * production's board, `GET /api/social/leaderboard/public?country=<the
 * visitor's country>` (`landingCountry`), the worker's edge-cached feed of
 * universities — never a person:
 *
 * - University, the default tab: that country's universities ranked by their
 *   students' mastery points, each behind its own logo when one was collected
 *   (`scripts/fetch-university-logos.ts`) and the DS crest otherwise. A
 *   country with too few ranked universities gets the worldwide board, and
 *   the caption says so.
 * - Global: ALWAYS the labelled sample (faces, "You" on top) — a public page
 *   shows no real student, masked or not (user 2026-09-30).
 *
 * The board is prefetched when the page goes idle (`prefetchLandingLeaderboard`
 * from the landing page), so it is there before the tile is; the tab switch
 * only flips which list shows. If the feed cannot answer (a dev server without
 * the social worker) or is empty, University shows its sample too, so the tile
 * never breaks.
 *
 * Sample portraits: StockSnap photos, all CC0 (public domain dedication — no
 * attribution or licence needed), face-cropped to 160px and self-hosted in
 * `public/assets/landing/avatars/`. Sources, for the record:
 *   amara  stocksnap.io/photo/smiling-woman-W6GFOSFAXA      (Matt Moloney)
 *   lucas  stocksnap.io/photo/smiling-man-3LMPSCJQGQ        (Matt Moloney)
 *   yuna   stocksnap.io/photo/person-smiling-QH2V8JROLS     (Matt Moloney)
 *   daniel stocksnap.io/photo/male-professional-6QXAIH13O6  (Kristin Hardwick)
 *   chloe  stocksnap.io/photo/smiling-woman-WD9GZ6WUOA      (Candace McDaniel)
 *   mateo  stocksnap.io/photo/male-portrait-NLX0RAPBGB      (Matt Moloney)
 *   giulia stocksnap.io/photo/education-graduation-XAL3MIM3OC (Candace McDaniel)
 *   diego  stocksnap.io/photo/confident-business-4Q1S53WMQ6 (Matt Moloney)
 *   imani  stocksnap.io/photo/smiling-woman-VTHPEAGBVR      (Matt Moloney)
 *   jonas  stocksnap.io/photo/man-thumbsup-ZUAZ22R9AL       (Bruce Mars)
 *   sofia  stocksnap.io/photo/smiling-woman-VXTSWFJO8X      (Matt Moloney)
 *   tom    stocksnap.io/photo/man-thumbsup-JEANSWX3A6       (YesManPro)
 */
import type { PublicLeaderboardFeed } from '@flashka/shared/social/types';
import { LeaderboardRankChip, SegmentedControl, Tooltip } from '@flashka/shared/ui';

import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import { motion, useReducedMotion } from 'framer-motion';
import { GlobeIcon, GraduationCapIcon, TrophyIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MasteryPointsIcon } from '@/components/study-groups/leaderboard/mp-icon';
import { MemberAvatar } from '@/components/study-groups/member-avatar';
import { cn } from '@/lib/utils';
import { isLang, LANG_COUNTRY } from '@/seo/marketing-routes';

type Scope = 'university' | 'global';

/** One row, whichever board it came from. */
type Row = {
  key: string;
  rank: number;
  /** Shown as-is; `null` is the sample's visitor ("You" / "Your university"). */
  name: string | null;
  masteryPoints: number;
  /** University rows: its self-hosted logo, when one was collected. */
  logo?: string;
  /** Global rows: a sample portrait in `public/assets/landing/avatars/`. */
  photo?: string;
  /** Global rows: how long ago the sample person was last active, for the
   *  avatar's hover tooltip — the app's activity dot (green now, amber hours,
   *  grey days), so the sample feels alive (user 2026-09-30). */
  activeMinutesAgo?: number;
};

/** Seven rows per board, like the frame. */
const ROWS = 7;

/** A self-hosted university logo, the same URL the live feed attaches. */
function sampleLogo(id: string, version: string) {
  return `/assets/landing/universities/${id}.webp?v=${version}`;
}

/** The sample, shown only when the live board cannot be: the visitor leads. */
const SAMPLE: Record<Scope, Row[]> = {
  // A real board's spread (user 2026-09-30): six figures at the top, falling
  // to four at the foot — not seven near-equal scores.
  global: [
    { name: null, masteryPoints: 184320, activeMinutesAgo: 0 },
    { name: 'Amara Okafor', photo: 'amara', masteryPoints: 127450, activeMinutesAgo: 4 },
    { name: 'Lucas Moreau', photo: 'lucas', masteryPoints: 86930, activeMinutesAgo: 150 },
    { name: 'Yuna Park', photo: 'yuna', masteryPoints: 52180, activeMinutesAgo: 25 },
    {
      name: 'Daniel Adeyemi',
      photo: 'daniel',
      masteryPoints: 23905,
      activeMinutesAgo: 420,
    },
    { name: 'Chloé Martin', photo: 'chloe', masteryPoints: 8740, activeMinutesAgo: 1560 },
    { name: 'Mateo Rossi', photo: 'mateo', masteryPoints: 1480, activeMinutesAgo: 4400 },
  ].map((r, i) => ({ ...r, key: `sample-${i}`, rank: i + 1 })),
  // The real top of the worldwide board on production (2026-10-01, MP
  // rounded), with the logos the live rows carry — so the fallback reads as
  // the board it stands in for, never invented scores for real schools. The
  // `?v=` hashes are the manifest's (`server/data/university-logos.generated.ts`;
  // pinned by the test). "Your university" leads, above them all.
  university: [
    { name: null, masteryPoints: 712400 },
    {
      name: 'Sapienza Università di Roma',
      logo: sampleLogo('IT-UNIV-26', 'e1faa53b'),
      masteryPoints: 653400,
    },
    {
      name: "St. George's University",
      logo: sampleLogo('GD-GNAB-20151011-03', '8d297edf'),
      masteryPoints: 442900,
    },
    {
      name: 'Università degli Studi di Napoli Federico II',
      logo: sampleLogo('IT-UNIV-18', 'f187f5d1'),
      masteryPoints: 372500,
    },
    {
      name: 'Università degli Studi di FIRENZE',
      logo: sampleLogo('IT-UNIV-10', 'a1d47962'),
      masteryPoints: 341100,
    },
    {
      name: 'UNISR - Università Vita Salute San Raffaele',
      logo: sampleLogo('IT-UNIV-A9', '85fe09c6'),
      masteryPoints: 306600,
    },
    {
      name: 'Università degli Studi "Magna Graecia" di CATANZARO',
      logo: sampleLogo('IT-UNIV-C5', '86cffad3'),
      masteryPoints: 286700,
    },
  ].map((r, i) => ({ ...r, key: `sample-${i}`, rank: i + 1 })),
};

/** A school without a collected logo: a DS-tinted tile with the cap. DS hues
 *  only (sky, not blue), so each inverts on the dark theme. */
const CREST_TONES = [
  'bg-primary-100 text-primary-600',
  'bg-red-100 text-red-600',
  'bg-sky-100 text-sky-600',
  'bg-amber-100 text-amber-600',
  'bg-green-100 text-green-600',
  'bg-pink-100 text-pink-600',
  'bg-orange-100 text-orange-600',
];

/**
 * Whose universities the board ranks: `?country=XX` on the page (QA — see
 * another country's board without a VPN), else the visitor's geolocated
 * country the middleware put in `<meta name="flashka-country">`, else the
 * page language's (`LANG_COUNTRY` — English is the UK; local dev has no geo).
 */
export function landingCountry(language: string): string {
  const valid = (v: string | null | undefined) => {
    const c = v?.trim().toUpperCase();
    return c && /^[A-Z]{2}$/.test(c) ? c : null;
  };
  if (typeof document !== 'undefined') {
    const fromUrl = valid(new URLSearchParams(window.location.search).get('country'));
    if (fromUrl) return fromUrl;
    const fromGeo = valid(
      document.querySelector<HTMLMetaElement>('meta[name="flashka-country"]')?.content,
    );
    if (fromGeo) return fromGeo;
  }
  const lang = language.split('-')[0];
  return LANG_COUNTRY[isLang(lang) ? lang : 'en'];
}

/** Production's board for `country` — one query for the page's life: the
 *  worker and the edge own freshness, so nothing here refetches it. */
function leaderboardQuery(country: string) {
  return queryOptions({
    queryKey: ['landing', 'leaderboard-public', country],
    staleTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<PublicLeaderboardFeed> => {
      // Default credentials, not 'omit': the route reads no cookie and the
      // edge caches it per URL either way, but a protected PR preview needs
      // the Vercel SSO cookie or the request is bounced to vercel.com.
      const res = await fetch(`/api/social/leaderboard/public?country=${country}`);
      if (!res.ok) throw new Error(`leaderboard-public ${res.status}`);
      const body = (await res.json()) as PublicLeaderboardFeed | { ok: false };
      if (!body.ok) throw new Error('leaderboard-public not ok');
      return body;
    },
  });
}

/** Start the board's fetch before its tile is anywhere near the screen. */
export function prefetchLandingLeaderboard(client: QueryClient, language: string) {
  return client.prefetchQuery(leaderboardQuery(landingCountry(language)));
}

/** A row's points. Hover (or focus) spells out "Mastery Points" in a small
 *  DS tooltip above — to a visitor with no account "MP" is an unexplained
 *  acronym (user 2026-09-30). */
function Mp({
  value,
  label,
  compact = false,
}: {
  value: number;
  label: string;
  /** A phone's tile: a smaller count, so the name keeps its words whole. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Tooltip content={t('studyGroups.leaderboard.row.mpTitle')} side="top">
      <button
        type="button"
        aria-label={`${value.toLocaleString()} ${t('studyGroups.leaderboard.row.mpTitle')}`}
        className={cn(
          'flex shrink-0 cursor-help items-center rounded-xl font-bold text-amber-500 transition-colors hover:bg-secondary-100',
          compact ? 'gap-0.5 p-1' : 'gap-1 p-2',
        )}>
        <MasteryPointsIcon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        <span className={compact ? 'text-[14px]' : 'text-[16px]'}>
          {value.toLocaleString()}
        </span>
        {/* The coin says it on a phone; the unit's letters go to the name. */}
        <span aria-hidden className={compact ? 'sr-only' : 'text-[12px]'}>
          {label}
        </span>
      </button>
    </Tooltip>
  );
}

/** A university's mark: its logo on a white plate (marks are drawn for a light
 *  ground, so the plate stays white in dark mode, like an app icon), or the
 *  crest when there is none or it fails to load. */
function UniversityMark({ row, highlight }: { row: Row; highlight: boolean }) {
  const [failed, setFailed] = useState(false);
  const ring = highlight && 'ring-2 ring-primary-400';
  if (row.logo && !failed) {
    return (
      <span
        aria-hidden
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-secondary-200 bg-surface-raised',
          ring,
        )}>
        <img
          src={row.logo}
          alt=""
          width={28}
          height={28}
          loading="lazy"
          decoding="async"
          className="h-7 w-7 object-contain"
          onError={() => setFailed(true)}
        />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl',
        CREST_TONES[(row.rank - 1) % CREST_TONES.length],
        ring,
      )}>
      <GraduationCapIcon className="h-5 w-5" />
    </span>
  );
}

/** A pulsing row while the live board is on its way. */
function PendingRow() {
  return (
    <li className="flex items-center justify-between rounded-[20px] py-2 pl-4 pr-2">
      <span className="flex min-w-0 flex-1 items-center gap-2.5">
        <span className="h-6 w-6 shrink-0 animate-pulse rounded-lg bg-secondary-100" />
        <span className="h-9 w-9 shrink-0 animate-pulse rounded-xl bg-secondary-100" />
        <span className="h-4 w-2/5 animate-pulse rounded-full bg-secondary-100" />
      </span>
      <span className="mr-2 h-4 w-16 animate-pulse rounded-full bg-secondary-100" />
    </li>
  );
}

export function LeaderboardWidget({
  play,
  className,
  compact = false,
}: {
  play: boolean;
  className?: string;
  /** The phone layout's narrow tile: tighter rows and a smaller count. */
  compact?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [scope, setScope] = useState<Scope>('university');

  // Resolved once: the page's URL and meta do not change under it.
  const [country] = useState(() => landingCountry(i18n.language));
  // Also enabled on arrival, in case the idle prefetch has not run yet.
  const feed = useQuery({ ...leaderboardQuery(country), enabled: play });

  const universities = useMemo<Row[]>(
    () =>
      (feed.data?.universities ?? []).slice(0, ROWS).map((u) => ({
        key: u.id ?? `${u.rank}`,
        rank: u.rank,
        name: u.name,
        masteryPoints: u.masteryPoints,
        logo: u.logo,
      })),
    [feed.data],
  );

  // Only the University tab is live; Global never waits on the feed.
  const pending = scope === 'university' && feed.isPending && play;
  const isLive = scope === 'university' && universities.length > 0;
  const rows = isLive ? universities : SAMPLE[scope];

  const caption = useMemo(() => {
    if (!isLive) return t('landingV3.rank.sample');
    if (feed.data?.universitiesScope !== 'country' || !feed.data.country) {
      return t('landingV3.rank.liveWorld');
    }
    let countryName = feed.data.country;
    try {
      countryName =
        new Intl.DisplayNames([i18n.language], { type: 'region' }).of(
          feed.data.country,
        ) ?? countryName;
    } catch {
      /* the code itself is still readable */
    }
    return t('landingV3.rank.liveCountry', { country: countryName });
  }, [feed.data, i18n.language, isLive, t]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={play ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
      transition={
        reduceMotion ? { duration: 0 } : { duration: 0.5, delay: play ? 0.5 : 0 }
      }
      aria-label={t('landingV3.rank.title')}
      className={cn(
        'mx-auto flex w-full max-w-[585px] flex-col gap-1.5 rounded-[36px] bg-secondary-100 px-1 pb-1 pt-1.5',
        className,
      )}>
      {/* Wraps on the narrowest phones (320px), the switch under the title. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-2.5 pl-4 pr-3">
        <span className="flex items-center gap-2 text-[14px] font-bold text-secondary-600">
          <TrophyIcon className="h-5 w-5" aria-hidden />
          {t('landingV3.rank.title')}
        </span>
        {/* Secondary (user 2026-09-28): the page keeps one primary, Get
            Started. University first, and selected: it is the default. */}
        <SegmentedControl<Scope>
          size="xxs"
          activeStyle="secondary"
          value={scope}
          onValueChange={setScope}
          accessibilityLabel={t('landingV3.rank.title')}
          items={[
            {
              value: 'university',
              icon: GraduationCapIcon,
              label: t('landingV3.rank.university'),
            },
            { value: 'global', icon: GlobeIcon, label: t('landingV3.rank.global') },
          ]}
        />
      </div>
      <ol
        aria-busy={pending || undefined}
        className={cn(
          'flex flex-col gap-1 rounded-[32px] bg-secondary-50',
          compact ? 'p-2' : 'p-4',
        )}>
        {pending
          ? Array.from({ length: ROWS }, (_, i) => <PendingRow key={i} />)
          : rows.map((row) => {
              const isYou = row.name === null;
              const name =
                row.name ??
                (scope === 'global'
                  ? t('landingV3.rank.you')
                  : t('landingV3.rank.yourUniversity'));
              return (
                <li
                  key={`${scope}-${row.key}`}
                  className={cn(
                    'flex items-center justify-between rounded-[20px] py-2',
                    compact ? 'pl-2.5 pr-1' : 'pl-4 pr-2',
                    row.rank === 1 && 'bg-secondary-50 shadow-card-row',
                  )}>
                  <span
                    className={cn(
                      'flex min-w-0 items-center',
                      compact ? 'gap-2' : 'gap-2.5',
                    )}>
                    <LeaderboardRankChip rank={row.rank} className="shrink-0" />
                    {scope === 'global' ? (
                      // The app's member avatar: hover shows the name and the
                      // activity dot. Nothing links — these are not people.
                      <span className="inline-flex shrink-0">
                        <MemberAvatar
                          name={name}
                          avatar={
                            row.photo
                              ? `/assets/landing/avatars/${row.photo}.jpg`
                              : undefined
                          }
                          size="lg"
                          tooltipSide="top"
                          lastActive={
                            row.activeMinutesAgo === undefined
                              ? undefined
                              : new Date(
                                  Date.now() - row.activeMinutesAgo * 60_000,
                                ).toISOString()
                          }
                          className={cn(isYou && 'ring-2 ring-primary-400')}
                        />
                      </span>
                    ) : (
                      <UniversityMark row={row} highlight={isYou} />
                    )}
                    <span
                      title={name}
                      className={cn(
                        'font-bold',
                        // A university's name is told apart at its END
                        // ("Università degli Studi di … PADOVA"): two lines,
                        // not one truncated to a shared prefix.
                        scope === 'university'
                          ? 'line-clamp-2 break-words text-[15px] leading-[19px]'
                          : 'truncate text-[16px]',
                        isYou ? 'text-primary-700' : 'text-secondary-600',
                      )}>
                      {name}
                    </span>
                  </span>
                  <Mp
                    value={row.masteryPoints}
                    label={t('landingV3.rank.mp')}
                    compact={compact}
                  />
                </li>
              );
            })}
      </ol>
      <span className="pb-1 text-center text-[11px] font-semibold text-secondary-400">
        {caption}
      </span>
    </motion.div>
  );
}
